
import React, { useState } from 'react';
import OnboardingLayout from '../../OnboardingLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MessageSquare, Headphones, Zap, BookOpen, Calendar, PieChart } from 'lucide-react';

interface FeatureTourProps {
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
}

const FeatureTour: React.FC<FeatureTourProps> = ({
  currentStep,
  totalSteps,
  onNext,
  onPrevious
}) => {
  const [currentTab, setCurrentTab] = useState('ai');
  
  const features = [
    {
      id: 'ai',
      name: 'AI Assistant',
      icon: MessageSquare,
      title: 'AI Reading Assistant',
      description: 'Get summaries, explanations, and insights powered by AI',
      details: [
        'Generate quick summaries of long articles',
        'Ask questions about the content',
        'Get key takeaways and insights',
        'Translate articles to other languages',
        'Create flashcards for important concepts'
      ]
    },
    {
      id: 'audio',
      name: 'Audio Articles',
      icon: Headphones,
      title: 'Listen to Articles',
      description: 'Convert any article to audio for on-the-go consumption',
      details: [
        'High-quality text-to-speech conversion',
        'Adjust playback speed and voice',
        'Download audio for offline listening',
        'Continue listening where you left off',
        'Audio queues for multiple articles'
      ]
    },
    {
      id: 'micropay',
      name: 'Micropayments',
      icon: Zap,
      title: 'Pay As You Read',
      description: 'Support journalists with small payments for individual articles',
      details: [
        'Pay only for what you actually read',
        'Flexible pricing from $0.25 to $2 per article',
        'Auto-pay for your favorite journalists',
        'Monthly spending caps',
        'Detailed payment history'
      ]
    },
    {
      id: 'library',
      name: 'Reading Library',
      icon: BookOpen,
      title: 'Organize Your Reading',
      description: 'Save, organize, and revisit your favorite content',
      details: [
        'Save articles for later reading',
        'Create custom collections and topics',
        'Highlight and annotate important passages',
        'Export content in various formats',
        'Reading progress tracking'
      ]
    },
    {
      id: 'digest',
      name: 'Weekly Digest',
      icon: Calendar,
      title: 'Personalized Digests',
      description: 'Get personalized content recommendations delivered to your inbox',
      details: [
        'Weekly summaries of top articles',
        'Personalized based on your interests',
        'New content from followed journalists',
        'Trending discussions and topics',
        'Upcoming live events and Q&As'
      ]
    },
    {
      id: 'insights',
      name: 'Reading Insights',
      icon: PieChart,
      title: 'Reading Analytics',
      description: 'Track your reading habits and discover patterns',
      details: [
        'Visualize your reading time and topics',
        'Discover your interests based on reading patterns',
        'See completion rates and engagement metrics',
        'Get personalized reading goals',
        'Expand your reading with diversity recommendations'
      ]
    }
  ];

  return (
    <OnboardingLayout
      title="Platform Features"
      subtitle="Discover the powerful tools that enhance your reading experience"
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={onNext}
      onPrevious={onPrevious}
    >
      <div className="max-w-4xl mx-auto">
        <Tabs 
          defaultValue="ai" 
          value={currentTab} 
          onValueChange={setCurrentTab}
          className="w-full"
        >
          <TabsList className="grid grid-cols-3 md:grid-cols-6 mb-6">
            {features.map(feature => (
              <TabsTrigger key={feature.id} value={feature.id} className="text-xs flex-col items-center gap-1 h-auto py-2">
                <feature.icon className="h-4 w-4" />
                <span>{feature.name}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          
          {features.map(feature => (
            <TabsContent key={feature.id} value={feature.id} className="space-y-6">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="md:w-1/2">
                  <div className="aspect-video bg-muted rounded-lg flex items-center justify-center border">
                    <div className="text-center p-6 flex flex-col items-center justify-center">
                      <feature.icon className="h-12 w-12 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Feature preview image</p>
                    </div>
                  </div>
                </div>
                
                <div className="md:w-1/2">
                  <Card>
                    <CardContent className="pt-6">
                      <h3 className="text-xl font-medium mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground mb-4">{feature.description}</p>
                      
                      <h4 className="text-sm font-medium mb-3">Key capabilities:</h4>
                      <ul className="space-y-2">
                        {feature.details.map((detail, index) => (
                          <li key={index} className="flex items-start text-sm">
                            <span className="text-primary mr-2">•</span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
        
        <div className="bg-muted/30 rounded-lg p-4 mt-6">
          <h4 className="text-sm font-medium mb-2">Exploring features:</h4>
          <p className="text-sm text-muted-foreground">
            You can access all these features from the article view and your personal dashboard.
            Premium features may require a subscription depending on your chosen plan.
          </p>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default FeatureTour;
